import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  Calendar,
  ClipboardList,
  Clock,
  CheckCircle,
  DollarSign
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import contractRenewalService from '../../services/contractRenewalService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ContractRenewal = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalRenewals: 0,
    draftStatus: 0,
    pendingApprovalStatus: 0,
    approvedStatus: 0,
    activeStatus: 0,
    rejectedStatus: 0,
    expiredStatus: 0,
    totalRenewalValue: 0,
    pendingRenewals: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [renewalsResponse, statsResponse] = await Promise.all([
        contractRenewalService.getAllContractRenewals(),
        contractRenewalService.getContractRenewalStats()
      ])
      
      if (renewalsResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (renewalsResponse.data || []).map(renewal => ({
          ...renewal,
          id: renewal._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (renewalsResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No contract renewal records found. Create your first contract renewal!')
        }
      } else {
        toast.error(renewalsResponse.message || 'Failed to load contract renewals')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading contract renewal records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view contract renewals')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load contract renewal records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Draft': 'inactive',
      'Pending Approval': 'pending',
      'Approved': 'active',
      'Rejected': 'danger',
      'Active': 'active',
      'Expired': 'inactive',
      'Cancelled': 'inactive'
    }
    return statusMap[status] || 'inactive'
  }

  const getRenewalTypeBadgeClass = (type) => {
    const typeMap = {
      'Renewal': 'active',
      'Extension': 'pending',
      'Amendment': 'warning',
      'Addendum': 'pending'
    }
    return typeMap[type] || 'inactive'
  }

  const isStartingSoon = (startDate) => {
    if (!startDate) return false
    const today = new Date()
    const start = new Date(startDate)
    const diffTime = start - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays > 0 && diffDays <= 30
  }

  const columns = [
    {
      accessorKey: 'renewalNumber',
      header: 'Renewal Number',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'originalContract.contractNumber',
      header: 'Original Contract',
      cell: ({ row }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">
            {row.original.originalContract?.contractNumber || '-'}
          </span>
          {row.original.originalContract?.contractTitle && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>
              {row.original.originalContract.contractTitle}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'partyName',
      header: 'Party',
      cell: ({ getValue, row }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue() || '-'}</span>
          {row.original.partyCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.partyCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'renewalType',
      header: 'Type',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getRenewalTypeBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'renewalStartDate',
      header: 'Start Date',
      cell: ({ getValue, row }) => {
        const date = getValue()
        const startingSoon = isStartingSoon(date)
        return (
          <div style={{ textAlign: 'center', color: startingSoon ? '#ffc107' : '#495057', fontWeight: startingSoon ? '600' : 'normal' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
            {startingSoon && <div style={{ fontSize: '11px', color: '#ffc107' }}>Starting Soon</div>}
          </div>
        )
      },
    },
    {
      accessorKey: 'renewalEndDate',
      header: 'End Date',
      cell: ({ getValue }) => {
        const date = getValue()
        return (
          <div style={{ textAlign: 'center' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'renewalValue',
      header: 'Renewal Value',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue(), row.original.currency || 'INR')}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getStatusBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/contracts/contract-renewal/form')
  }

  const handleEdit = (renewalData) => {
    navigate('/contracts/contract-renewal/edit', { state: { renewalData } })
  }

  const handleView = (renewalData) => {
    navigate('/contracts/contract-renewal/view', { state: { renewalData } })
  }

  const handleDelete = (renewalData) => {
    setDeleteItem(renewalData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting contract renewal...')
      const response = await contractRenewalService.deleteContractRenewal(deleteItem._id)
      if (response.success) {
        toast.success('Contract renewal deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete contract renewal')
      }
    } catch (error) {
      console.error('Error deleting contract renewal:', error)
      if (error.response?.status === 404) {
        toast.error('Contract renewal not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this contract renewal')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete contract renewal. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Contracts', path: '/contracts' },
          { label: 'Contract Renewals', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Contract Renewals</h1>
          <p>Contract renewal and extension management</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Renewal
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <ClipboardList size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalRenewals || 0}</div>
          <div className="metric-title">Total Renewals</div>
          <div className="metric-subtitle">All renewal records</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Pending</span>
            </div>
          </div>
          <div className="metric-value">{stats.pendingApprovalStatus || 0}</div>
          <div className="metric-title">Pending Approval</div>
          <div className="metric-subtitle">Awaiting review</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Approved</span>
            </div>
          </div>
          <div className="metric-value">{stats.approvedStatus || 0}</div>
          <div className="metric-title">Approved</div>
          <div className="metric-subtitle">Approved renewals</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Calendar size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeStatus || 0}</div>
          <div className="metric-title">Active</div>
          <div className="metric-subtitle">Currently active</div>
        </div>

        <div className="metric-card conversion">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Soon</span>
            </div>
          </div>
          <div className="metric-value">{stats.pendingRenewals || 0}</div>
          <div className="metric-title">Starting Soon</div>
          <div className="metric-subtitle">Upcoming renewals</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Value</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(stats.totalRenewalValue)}</div>
          <div className="metric-title">Total Value</div>
          <div className="metric-subtitle">All renewals combined</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Contract Renewal"
        message={`Are you sure you want to delete contract renewal "${deleteItem?.renewalNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ContractRenewal

